import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchInvoices,
  createInvoice,
  createInvoiceItems,
  updateInvoice,
  deleteInvoice,
  deleteInvoiceFile,
  deleteInvoiceItemsByInvoiceId,
  uploadInvoiceFile,
  processInvoiceOCR,
  fetchRecurringExpenses,
  createRecurringExpense,
  updateRecurringExpense,
  deleteRecurringExpense,
  generateInvoiceJournalEntry,
  fetchInvoiceById,
  createTreasuryTransaction,
  fetchTransactionsByInvoiceId,
  updateTreasuryTransaction,
  deleteTreasuryTransactionByInvoiceId
} from "../../services/accounting";
import { getCurrentUser, getProfilesByIds } from "../../services/auth";
import * as pdfjsLib from 'pdfjs-dist';
import { toast } from "react-toastify";
import { setProcessingStatus } from "./accountingSlice";

// Set worker via CDN to avoid ejecting CRA build config
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

export const getInvoices = createAsyncThunk(
  "accounting/fetchInvoices",
  async (_, thunkAPI) => {
    try {
      const res = await fetchInvoices();
      if (res.error) throw res.error;

      // Enrich with uploader profile info
      const userIds = [
        ...new Set(res.data.map((inv) => inv.uploaded_by).filter(Boolean)),
      ];

      if (userIds.length > 0) {
        const { data: profiles } = await getProfilesByIds(userIds);

        if (profiles) {
          const profileMap = {};
          profiles.forEach((p) => {
            profileMap[p.id] = p;
          });

          return res.data.map((inv) => ({
            ...inv,
            uploader_name: inv.uploaded_by && profileMap[inv.uploaded_by]
              ? `${profileMap[inv.uploaded_by].first_name || ""} ${profileMap[inv.uploaded_by].last_name || ""}`.trim() ||
                profileMap[inv.uploaded_by].email
              : null,
          }));
        }
      }

      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const uploadAndProcessInvoice = createAsyncThunk(
  "accounting/uploadAndProcess",
  async ({ file, useOCR, flow_type }, thunkAPI) => {
    try {
      const {
        data: { user },
      } = await getCurrentUser();
      if (!user) throw new Error("Not authenticated");

      // Step 1: Read file locally as Base64 (avoid uploading to bucket yet)
      thunkAPI.dispatch(setProcessingStatus("uploading"));
      
      let base64Str = "";
      
      if (file.type === "application/pdf") {
        // Convert PDF first page to JPEG Data URL for OpenAI Vision
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 2.0 }); // Sharp resolution
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        await page.render({ canvasContext: context, viewport }).promise;
        base64Str = canvas.toDataURL("image/jpeg", 0.9);
      } else {
        // Standard image fallback
        base64Str = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      }

      // If user disabled OCR, skip directly
      if (!useOCR) {
        return {
          data: {},
          confidence: {},
          fileObj: file,
        };
      }

      // Step 2: Process OCR
      thunkAPI.dispatch(setProcessingStatus("processing_ocr"));
      await new Promise((resolve) => setTimeout(resolve, 800)); // UX delay

      // Step 3: Analyze data
      thunkAPI.dispatch(setProcessingStatus("analyzing"));
      const ocrResult = await processInvoiceOCR(null, file.name, base64Str, flow_type);
      await new Promise((resolve) => setTimeout(resolve, 800)); // UX delay

      // We still return file to the frontend state, so we temporarily pass the File object
      // so it can be uploaded later during handleSave
      return {
        ...ocrResult,
        fileObj: file,
      };
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const saveInvoice = createAsyncThunk(
  "accounting/saveInvoice",
  async ({ invoiceData, items, fileObj, receiptFileObj, receiptFileObjs }, thunkAPI) => {
    try {
      const result = await toast.promise(
        (async () => {
          let file_url = invoiceData.file_url;
          let file_name = invoiceData.file_name;
          let payment_receipt_url = invoiceData.payment_receipt_url || null;
          let payment_receipt_name = invoiceData.payment_receipt_name || null;

          // Upload supplier invoice file
          if (fileObj) {

             const uploadResult = await uploadInvoiceFile(fileObj, invoiceData.user_id);
             file_url = uploadResult.url;
             file_name = uploadResult.fileName;

          }

          // Upload payment receipt files (proof of payment by the company)
          const filesToUpload = receiptFileObjs?.length > 0 
            ? receiptFileObjs 
            : (receiptFileObj ? [receiptFileObj] : []);

          if (filesToUpload.length > 0) {
            const uploadedUrls = [];
            const uploadedNames = [];
            for (const f of filesToUpload) {
              const receiptResult = await uploadInvoiceFile(f, invoiceData.user_id);
              if (receiptResult?.url) {
                uploadedUrls.push(receiptResult.url);
                uploadedNames.push(receiptResult.fileName || f.name);
              }
            }
            payment_receipt_url = uploadedUrls.join(',');
            payment_receipt_name = uploadedNames.join(',');
          }

          const invoicePayload = { 
            ...invoiceData, 
            file_url, 
            file_name, 
            payment_receipt_url, 
            payment_receipt_name,
            issue_date: invoiceData.issue_date?.trim() === '' ? null : invoiceData.issue_date,
            travel_date: invoiceData.travel_date?.trim() === '' ? null : invoiceData.travel_date,
          };

          const bankAccountId = invoicePayload.paid_from_bank_account || null;
          const paymentDateVal = invoicePayload.payment_date || null;
          delete invoicePayload.payment_date;

          // Create the invoice (paid_from_bank_account is now a real column)

          const { data: invoice, error: invError } = await createInvoice(invoicePayload);
          if (invError) {
            console.error("[saveInvoice] Invoice creation error:", invError);
            throw invError;
          }


          // Deduzco del banco si es compra al contado (bankAccountId)
          if (bankAccountId) {
             const isVenta = invoicePayload.flow_type === 'VENTA';
             const txType = isVenta ? 'INBOUND' : 'OUTBOUND';
             const txDesc = isVenta 
                 ? 'Cobro de Venta: ' + (invoicePayload.supplier_name || invoicePayload.document_number)
                 : 'Pago de Comprobante / Compra: ' + (invoicePayload.supplier_name || invoicePayload.document_number);

             const { error: txError } = await createTreasuryTransaction({
                 bank_account_id: bankAccountId,
                 transaction_type: txType,
                 amount: invoicePayload.total,
                 description: txDesc,
                 reference_invoice_id: invoice.id,
                 transaction_date: paymentDateVal || invoicePayload.issue_date || new Date().toISOString()
             });
             if (txError) console.error("[saveInvoice] Treasury tx error:", txError);
          }

          // Create items linked to invoice
          if (items && items.length > 0) {
            const itemsWithInvoiceId = items.map((item, index) => ({
              invoice_id: invoice.id,
              code: item.code || '',
              description: item.description,
              quantity: parseFloat(item.quantity) || 0,
              unit_price: parseFloat(item.unit_price) || 0,
              total: parseFloat(item.total) || 0,
              afectacion_igv: item.afectacion_igv || '10',
              sort_order: index,
            }));

            const { error: itemsError } = await createInvoiceItems(itemsWithInvoiceId);
            if (itemsError) {
              console.error("[saveInvoice] Items creation error:", itemsError);
              throw itemsError;
            }
          }

          // Fetch the complete invoice with items
          const { data: fullInvoice } = await fetchInvoiceById(invoice.id);

          // Auto-generate journal entry if status is confirmed
          if (invoicePayload.status === 'confirmed' && fullInvoice) {
            try {
              const entry = await generateInvoiceJournalEntry(fullInvoice);
              if (entry) {

              }
            } catch (journalErr) {
              console.warn("[saveInvoice] Auto-journal failed (non-blocking):", journalErr);
            }
          }

          return fullInvoice;
        })(),
        {
          pending: { render: () => "Guardando comprobante...", icon: false },
          success: {
            render: () => "Comprobante guardado correctamente 😏",
            icon: "🚀",
          },
          error: { render: ({ data }) => {
            console.error("[saveInvoice] Full error:", data);
            return `Error: ${data?.message || data?.details || 'Error al guardar el comprobante'}`;
          }},
        }
      );

      return result;
    } catch (err) {
      console.error("[saveInvoice] Caught error:", err);
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const updateInvoiceThunk = createAsyncThunk(
  "accounting/updateInvoice",
  async ({ id, invoiceData, items }, thunkAPI) => {
    try {
      const result = await toast.promise(
        (async () => {
          // Update the invoice
          const sanitizedInvoiceData = {
            ...invoiceData,
            issue_date: invoiceData.issue_date?.trim() === '' ? null : invoiceData.issue_date,
            travel_date: invoiceData.travel_date?.trim() === '' ? null : invoiceData.travel_date,
          };
          delete sanitizedInvoiceData.payment_date;
          const { error: invError } = await updateInvoice(id, sanitizedInvoiceData);
          if (invError) throw invError;

          // Handle decoupled treasury transaction updates
          const { data: existingTxs } = await fetchTransactionsByInvoiceId(id);
          const bankAccountId = invoiceData.paid_from_bank_account || null;

          if (existingTxs && existingTxs.length > 0) {
            const tx = existingTxs[0];
            if (bankAccountId) {
              // Update existing transaction
              const isVenta = invoiceData.flow_type === 'VENTA';
              const txType = isVenta ? 'INBOUND' : 'OUTBOUND';
              const txDesc = isVenta 
                  ? 'Cobro de Venta: ' + (invoiceData.supplier_name || invoiceData.document_number)
                  : 'Pago de Comprobante / Compra: ' + (invoiceData.supplier_name || invoiceData.document_number);

              await updateTreasuryTransaction(tx.id, {
                bank_account_id: bankAccountId,
                transaction_type: txType,
                amount: parseFloat(invoiceData.total) || 0,
                description: txDesc,
                transaction_date: invoiceData.payment_date || tx.transaction_date || invoiceData.issue_date || new Date().toISOString()
              });
            } else {
              // User cleared the bank account, delete the transaction
              await deleteTreasuryTransactionByInvoiceId(id);
            }
          } else if (bankAccountId) {
            // No existing transaction but bank is selected, create new
            const isVenta = invoiceData.flow_type === 'VENTA';
            const txType = isVenta ? 'INBOUND' : 'OUTBOUND';
            const txDesc = isVenta 
                ? 'Cobro de Venta: ' + (invoiceData.supplier_name || invoiceData.document_number)
                : 'Pago de Comprobante / Compra: ' + (invoiceData.supplier_name || invoiceData.document_number);

            await createTreasuryTransaction({
              bank_account_id: bankAccountId,
              transaction_type: txType,
              amount: parseFloat(invoiceData.total) || 0,
              description: txDesc,
              reference_invoice_id: id,
              transaction_date: invoiceData.payment_date || invoiceData.issue_date || new Date().toISOString()
            });
          }

          // Replace items: delete existing, insert new
          await deleteInvoiceItemsByInvoiceId(id);

          if (items && items.length > 0) {
            const itemsWithInvoiceId = items.map((item, index) => ({
              invoice_id: id,
              code: item.code || '',
              description: item.description,
              quantity: parseFloat(item.quantity) || 0,
              unit_price: parseFloat(item.unit_price) || 0,
              total: parseFloat(item.total) || 0,
              afectacion_igv: item.afectacion_igv || '10',
              sort_order: index,
            }));

            const { error: itemsError } = await createInvoiceItems(itemsWithInvoiceId);
            if (itemsError) throw itemsError;
          }

          // Fetch the complete updated invoice
          const { data: fullInvoice } = await fetchInvoiceById(id);

          // Auto-generate journal entry if status changed to confirmed
          if (invoiceData.status === 'confirmed' && fullInvoice && !fullInvoice.journal_entry_id) {
            try {
              const entry = await generateInvoiceJournalEntry(fullInvoice);
              if (entry) { /* journal entry created */ }
            } catch (journalErr) {
              console.warn("[updateInvoice] Auto-journal failed (non-blocking):", journalErr);
            }
          }

          return fullInvoice;
        })(),
        {
          pending: { render: () => "Actualizando comprobante...", icon: false },
          success: {
            render: () => "Comprobante actualizado correctamente 😏",
            icon: "🚀",
          },
          error: { render: () => "Error al actualizar el comprobante" },
        }
      );

      return result;
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const removeInvoice = createAsyncThunk(
  "accounting/removeInvoice",
  async ({ id, filePath }, thunkAPI) => {
    try {
      await toast.promise(
        (async () => {
          // Delete file from storage if exists
          if (filePath) {
            await deleteInvoiceFile(filePath);
          }
          // Delete any linked treasury transactions first to maintain integrity
          await deleteTreasuryTransactionByInvoiceId(id);
          // Delete invoice (cascade deletes items)
          const { error } = await deleteInvoice(id);
          if (error) throw error;
        })(),
        {
          pending: { render: () => "Eliminando comprobante...", icon: false },
          success: {
            render: () => "Comprobante eliminado correctamente 😏",
            icon: "🚀",
          },
          error: { render: () => "Error al eliminar el comprobante" },
        }
      );

      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

// ============================================
// Recurring Expenses Thunks
// ============================================

export const getRecurringExpenses = createAsyncThunk(
  "accounting/fetchRecurringExpenses",
  async (_, thunkAPI) => {
    try {
      const res = await fetchRecurringExpenses();
      if (res.error) throw res.error;
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const addRecurringExpense = createAsyncThunk(
  "accounting/addRecurringExpense",
  async (expenseData, thunkAPI) => {
    try {
      const result = await toast.promise(
        createRecurringExpense(expenseData).then((res) => {
          if (res.error) throw res.error;
          return res;
        }),
        {
          pending: "Creando gasto recurrente...",
          success: "Gasto recurrente programado 😎",
          error: {
            render({ data }) {
              return `Error: ${data?.message || 'Error al crear gasto recurrente'}`;
            }
          }
        }
      );

      // Refresh the full list to get enriched data with provider joins
      thunkAPI.dispatch(getRecurringExpenses());
      return result.data?.[0] || null;
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const editRecurringExpense = createAsyncThunk(
  "accounting/editRecurringExpense",
  async ({ id, data }, thunkAPI) => {
    try {
      const result = await toast.promise(
        updateRecurringExpense(id, data).then((res) => {
          if (res.error) throw res.error;
          return res;
        }),
        {
          pending: "Actualizando...",
          success: "Gasto actualizado adecuadamente",
          error: {
            render({ data }) {
              return `Error: ${data?.message || 'Error al actualizar'}`;
            }
          }
        }
      );
      return result.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);

export const removeRecurringExpense = createAsyncThunk(
  "accounting/removeRecurringExpense",
  async (id, thunkAPI) => {
    try {
      await toast.promise(
        deleteRecurringExpense(id).then((res) => {
          if (res.error) throw res.error;
          return res;
        }),
        {
          pending: "Eliminando...",
          success: "Gasto recurrente eliminado",
          error: {
            render({ data }) {
              return `Error: ${data?.message || 'Error al eliminar'}`;
            }
          }
        }
      );
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue({ error: err.message });
    }
  }
);
